/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import "./AddClusterModal.css";
import { Dialog, Button, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

const AddClusterModal = ({ open, handleClose, addNewClusters, existingClusters = [] }) => {
  const [addNewClusterNumber, setAddNewClusterNumber] = useState("");
  const [disableSave, setDisableSave] = useState(true);
  const [isDuplicate, setIsDuplicate] = useState(false);
  
  const checkCluster = ($event) => {
    const value = $event.target.value;
    const numericValue = Number(value);
    const isValidNumber = typeof numericValue === "number" && !Number.isNaN(numericValue) && numericValue >= 0 && value.trim() !== "";
    const isDuplicateCluster = existingClusters.some(cluster => Number(cluster) === numericValue);
    
    setAddNewClusterNumber(value);
    setIsDuplicate(isDuplicateCluster && isValidNumber);
    
    if (isValidNumber && !isDuplicateCluster) {
      setDisableSave(false);
    } else {
      setDisableSave(true);
    }
  };
  
  useEffect(() => {
    if (open) {
      setAddNewClusterNumber("");
      setDisableSave(true);
      setIsDuplicate(false);
    }
  }, [open]);

  const sendNewCluster = () => {
    addNewClusters(addNewClusterNumber * 1);
    handleClose();
  };

  return (
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            width: "540px",
            minHeight: "253px",
            borderRadius: "8px",
            padding: "24px",
            border: "1px",
            backgroundColor: "#FFFFFF",
            gap: "24px",
          },
        }}
        open={open}
        onClose={handleClose}
      >
        <div className="popupHeader">
          <Typography
            sx={{
              minWidth: "116px",
              minHeight: "26px",
              fontWeight: 500,
              fontSize: "22px",
              lineHeight: "120%",
            }}
          >
            Add Cluster
          </Typography>

          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="popupContent">
          <div className="popupContent-firstchild">
            <Typography
              sx={{
                fontSize: "16px",
                height: "19px",
                minWidth: "85px",
                fontWeight: "600",
                lineHeight: "80%",
                letterSpacing: "0px",
              }}
            >
              Add Cluster
            </Typography>
            <input
              type="number"
              placeholder="Ex: 41, 42"
              className="inputCluster no-arrows"
              value={addNewClusterNumber}
              onChange={($event) => checkCluster($event)}
            />
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                maxHeight: "14px",
                maxWidth: "492px",
                fontSize: "12px",
                fontWeight: "400",
                lineHeight: "100%",
                letterSpacing: "0px",
                color: isDuplicate ? "#FF0000" : "inherit",
              }}
            >
              {isDuplicate ? "Duplicate cluster not allowed" : "Negative values are not allowed"}
            </Typography>
          </div>
          <div className="popupContent-secondchild">
            <div className="btn-grp">
              <Button
                className="btn"
                sx={{
                  backgroundColor: "#1214151A !important",
                  color: "#2A2C2D !important",
                }}
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                className="btn"
                sx={{
                  backgroundColor: disableSave
                    ? "#C2DFFB !important"
                    : "#0476E2 !important",
                  color: "#FFFFFF !important",
                }}
                disabled={disableSave}
                onClick={sendNewCluster}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
  );
};

AddClusterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  addNewClusters: PropTypes.func.isRequired,
  existingClusters: PropTypes.array,
};

export default AddClusterModal;
